/** @file Deform timelines: rigid offsets in the compiled vertex frame; skinned ones per influence. */
import { throwError } from '@pose2d/format/throwError';
import type { AnimationDoc, Doc, MeshAtt, TimelineJson } from '@pose2d/format/types';
import { affine } from '@pose2d/math/affine';
import type { Affine } from '@pose2d/math/types';
import { byCompiledIndex } from './byCompiledIndex';
import { deformToInfluences } from './deformToInfluences';
import { inverseBindPoses } from './inverseBindPoses';
import { mountMatrix } from './mountMatrix';
import { requireAttachmentImage } from './requireAttachmentImage';
import { requireCompiledIndex } from './requireCompiledIndex';
import { segmentCurves } from './segmentCurves';
import { sortKeysByFrame } from './sortKeysByFrame';

type Range = { start: number; values: number[] };

/** Offset pairs (vectors) through the linear part of `m`: a mounted mesh's vertices are m · authored vertices. */
function throughMount(values: readonly number[], m: Affine): number[] {
  const out: number[] = [];
  const p: [number, number] = [0, 0];
  for (let i = 0; i + 1 < values.length; i += 2) {
    affine.applyVec(m, values[i] ?? 0, values[i + 1] ?? 0, p);
    out.push(p[0], p[1]);
  }
  return out;
}

/**
 * The per-key converter of one mesh: a copy for rigid meshes (through the mount's linear part when mounted, since
 * meshRecord bakes the vertices through the mount), the §6.7 rewrite for skinned ones.
 */
function rangeConverter(doc: Doc, id: string, mesh: MeshAtt, path: string): (o: Range) => Range {
  const g = mesh.geometry;
  if (g.kind === 'skinned') {
    const inverse = inverseBindPoses(mesh, path);
    return (o) => deformToInfluences(g, inverse, o);
  }
  const mount = mountMatrix(doc, id, mesh, requireAttachmentImage(doc, id, mesh));
  return mount === null ? (o) => ({ start: o.start, values: [...o.values] }) : (o) => ({ start: o.start, values: throughMount(o.values, mount) });
}

/** One timeline per deformed mesh (compiled attachment order); a null key is an empty range; curves in progress form. */
export function deformTimelines(doc: Doc, anim: AnimationDoc, fps: number, attachmentIndex: ReadonlyMap<string, number>, at: string): TimelineJson[] {
  const out: TimelineJson[] = [];
  for (const [id, raw] of byCompiledIndex(anim.deform, attachmentIndex)) {
    const path = `${at}.deform.${id}`;
    const mesh = doc.attachments[id];
    if (mesh?.type !== 'mesh') throwError('VALIDATION', undefined, { path, hint: 'deform keys need a mesh attachment' });
    const keys = sortKeysByFrame(raw);
    if (keys.length === 0) continue;
    const toRange = rangeConverter(doc, id, mesh, `attachments.${id}.geometry`);
    const ranges = keys.map((k): Range => (k.offsets === null ? { start: 0, values: [] } : toRange(k.offsets)));
    const c = segmentCurves(keys, null);
    out.push({
      k: 'deform', att: requireCompiledIndex(attachmentIndex, id, 'attachment', path),
      t: keys.map((k) => k.f / fps), start: ranges.map((r) => r.start), v: ranges.map((r) => r.values),
      ...(c === null ? {} : { c }),
    });
  }
  return out;
}
